import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreadcrumbs, useUser } from '../hooks';
import { useParams } from 'react-router-dom';
import { Header, Table, Groupper, Toolbar, Toast, Modal, Message, Button, Icon, Label } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import { Expense, SetLoading, Asset, ExpenseRejectReason } from '../SiteClasses';
import { bindClose, addCommas, bindClick, getDepartmentName } from '../SiteUtil';
import { AddAssetModal, AssetViewModal, UserLog, ExpenseModal } from '../components'
import classNames from 'classnames';
import moment from 'moment';
import UserAccess from '../UserAccess';
import API from '../API';

enum Modals{
	EDIT = 1,
	PAID = 2,
	DELETE = 3,
	APPROVE = 4,
	ADD_ASSET = 5,
	VIEW_ASSET = 6,
	DELETE_ASSET = 7,
	REJECT = 8,
	UNPAID = 9
}

var ExpenseView = ()=>{
	var { user, hasAccess } = useUser();
	var params = useParams<{ id: string }>();
	var { t } = useTranslation();
	var { setTitle } = useBreadcrumbs();
	var [loadError, setLoadError] = useState<string>(null);
	var [expenseData, setExpenseData] = useState<Expense>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>([]);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [selectedAsset, setSelectedAsset] = useState<Asset>(null);
	var [rejectReasonId, setRejectReasonId] = useState<number>(null);
	var [rejectReasons, setRejectReasons] = useState<ExpenseRejectReason[]>([]);
	
	useEffect(()=>{
		API.getExpense(parseInt(params.id)).then(res=>{
			if(res.error) setLoadError(res.message);
			setTitle('Gasto ' + res.data.expense_id.toString());
			setExpenseData(res.data);
		}).catch(err=>{
			setLoadError('Hubo un error cargando el gasto (LCL-1)');
		})
		API.getExpenseRejectReasons().then(res=>{
			if(!res.error) setRejectReasons(res.data);
		}).catch(()=>{});
	}, []);

	var deleteExpense = (setLoading: SetLoading)=>{
		setLoading(true);
		API.deleteExpense(expenseData.expense_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Gasto eliminado!');
			
			var e = { ...expenseData };
			e.deleted = true;
			setExpenseData(e);
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado eliminando el gasto. (LCL-1)']);
		}).finally(()=>{	
			setLoading(false);
		})
	}

	var paid = (setLoading: SetLoading)=>{
		setLoading(true);
		API.paidExpense(expenseData.expense_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Gasto pagado!');
			
			var e = { ...expenseData };
			e.paid = true;
			setExpenseData(e);
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado pagando el gasto. (LCL-1)']);
		}).finally(()=>{	
			setLoading(false);
		})
	}

	var unpaid = (setLoading: SetLoading)=>{
		setLoading(true);
		API.paidExpense(expenseData.expense_id, false).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Gasto desmarcado como pagado!');
			
			var e = { ...expenseData };
			e.paid = false;
			e.user_paid = null;
			e.user_paid_name = null;
			setExpenseData(e);
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado desmarcando el gasto. (LCL-1)']);
		}).finally(()=>{	
			setLoading(false);
		})
	}

	var approve = (setLoading: SetLoading)=>{
		setLoading(true);
		API.approveExpense([expenseData.expense_id]).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Presupuesto aprobado!');

			var e = {...expenseData};
			e.approved = true;
			e.user_approved = user.user_id;
			e.user_approver_name = (user.first_name + ' ' +user.last_name);
			setExpenseData(e);
			setShownModal(null);
		}).catch(err=>{
			Toast.error('Hubo un error inesperado actualizando el gasto. (LCL-1)');
		}).finally(()=>{	
			setLoading(false);
		})
	}

	var updateExpense = (e: Expense)=>{
		setExpenseData(e);
		setShownModal(null);
	}

	var rejectExpense = (setLoading: SetLoading)=>{
		if(!rejectReasonId){
			return setModalPrompts(['Selecciona un motivo de rechazo.']);
		}
		setLoading(true);
		API.rejectExpense(expenseData.expense_id, rejectReasonId).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Gasto rechazado!');

			var e = { ...expenseData };
			e.rejected = true;
			e.reject_reason = rejectReasonId;
			e.reject_reason_name = rejectReasons.find(r => r.reason_id === rejectReasonId)?.reason || '';
			e.approved = false;
			setExpenseData(e);
			setShownModal(null);
			setRejectReasonId(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado rechazando el gasto. (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var addAsset = (newAsset: Asset)=>{
		var e = {...expenseData};
		if(!e.assets) e.assets = [];
		e.assets.push(newAsset);
		setExpenseData(e);
		setShownModal(null);
	}

	var onAssetDeleted = (asset_id: number)=>{
		var e = { ...expenseData };
		e.assets = e.assets.filter(asset => asset.asset_id !== asset_id);
		setExpenseData(e);
	};

	if(loadError) return <Header text='Error' subtext={loadError} />;
   if(!expenseData) return <Header text='Cargando datos' loading/>;
	
	return <div>
		{expenseData.rejected && !expenseData.paid ? (
			<Message type='warning' style={{ maxWidth: 500, margin: 'auto', marginTop: 20, marginBottom: -5 }} centered header='Gasto rechazado' text={expenseData.reject_reason_name} />
		) : null}
		<Groupper style={{ maxWidth: 500, marginTop: 20 }} title={'Datos de gasto'}>
			<Toolbar stretch>
				{!expenseData.deleted && !expenseData.paid && hasAccess(UserAccess.EVENTS.CREATE_EXPENSE) ? <Toolbar.Item text='Editar' iconName='pencil' onClick={() => setShownModal(Modals.EDIT)}/> : null }
				{!expenseData.deleted && !expenseData.paid && expenseData.approved && hasAccess(UserAccess.ACCOUNTING.PAY_EXPENSE) ? <Toolbar.Item text='Marcar pagado' iconName='money-bill' onClick={() => setShownModal(Modals.PAID)} /> : null}
				{!expenseData.deleted && expenseData.paid && hasAccess(UserAccess.ACCOUNTING.PAY_EXPENSE) ? <Toolbar.Item text='No pagado' iconName='undo' onClick={() => setShownModal(Modals.UNPAID)} /> : null}
				{!expenseData.approved && !expenseData.deleted && hasAccess(UserAccess.EVENTS.APPROVE_EXPENSE) && <Toolbar.Item text='Aprobar' iconName='check' onClick={() => setShownModal(Modals.APPROVE)} />}
				{!expenseData.deleted && !expenseData.rejected && !expenseData.paid && hasAccess(UserAccess.ACCOUNTING.REJECT_EXPENSE) && <Toolbar.Item text='Rechazar' iconName='ban' onClick={() => setShownModal(Modals.REJECT)} />}
				{!expenseData.deleted && hasAccess(UserAccess.EVENTS.DELETE_EXPENSE) && <Toolbar.Item text='Eliminar' iconName='close' onClick={() => setShownModal(Modals.DELETE)}/>}
			</Toolbar>
			<Table fitted details className='last' style={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
				<Table.Row data={['ID', expenseData.expense_id]}/>
				<Table.Row data={['Titulo', expenseData.expense_title]}/>			
				<Table.Row data={['Tipo', expenseData.type_name]}/>
				<Table.Row data={['Método de pago', expenseData.method_name || <Icon name='minus'/>]}/>
				<Table.Row data={['Departamento', expenseData.department_name ? 
					<Label style={{ width: 110 }} color='orange'>{getDepartmentName(expenseData.department_id)}</Label> : <Icon name='minus'/>]}
				/>				
				{expenseData.date_id ? <Table.Row data={['Evento', expenseData.event_name]}/> : null}
				<Table.Row data={['Creado', moment.unix(expenseData.date_created).format('DD/MMM/YY HH:mm')]}/>
				<Table.Row data={['Creador', expenseData.user_creator_name]}/>
				<Table.Row data={['Monto', '$' + addCommas(expenseData.amount)]}/>
				<Table.Row data={['Estatus', (
					<div className={classNames("fr label", {
						green: expenseData.paid && !expenseData.deleted,
						blue: expenseData.approved && !expenseData.deleted && !expenseData.paid,
						red: expenseData.deleted || (expenseData.rejected && !expenseData.paid),
						yellow: !expenseData.paid && !expenseData.deleted && !expenseData.approved && !expenseData.rejected
					})}>
						{expenseData.deleted ? 'Eliminado' : expenseData.paid ? 'Pagado' : expenseData.approved ? 'Aprobado' : expenseData.rejected ? 'Rechazado' : 'No Pagado'}
					</div>
				)]}/>
				<Table.Row data={['Descripción', expenseData.description ? expenseData.description : <Icon name='minus'/>]}/>
			</Table>
		</Groupper>
		{expenseData.account ? <Table 
			details
			striped
			title='Cuenta Bancaria'
			style={{ maxWidth: 500, margin: 'auto', marginTop: 15 }}
			data={[
				['ID', expenseData.account.account_id],
				['Propietario', expenseData.account.name_account],
				['Correo', expenseData.account.email_account || <Icon name='minus'/>],
				['RFC', expenseData.account.rfc],
				['Código postal', expenseData.account.postal_code],
				null,
				['Tipo de cuenta', expenseData.account.card_number ? <><Icon name='credit-card'/>Tarjeta bancaria</> : <><Icon name='landmark'/>Transferencia bancaria (SPEI)</>],
				['Banco', <>
						{expenseData.account.bank}
						<div className="meta">P: {expenseData.account.bank_participant}</div>
				</>],
				[expenseData.account.card_number ?  'Tarjeta Bancaria' : 'CLABE', expenseData.account.card_number ? expenseData.account.card_number : expenseData.account.clabe],
			]}
		/> : null}
		<Table
			fitted
			striped
			title='Evidencias'
			headers={['ID', 'Nombre', 'Tipo', 'Creacion', 'Creador', <Icon name='wrench' />]}
			collapsingIndexes={[0, 5]}
			style={{ maxWidth: 700, margin: 'auto', marginTop: 20 }}
			button={<>
				<Button text='Agregar' color='black' iconName='plus-circle' size='small' onClick={bindClick(setShownModal, Modals.ADD_ASSET)}/>
			</>}
		>
			{expenseData?.assets && expenseData.assets.length !== 0 ? (
				expenseData.assets.map(a => (
					<Table.Row
						key={a.asset_id}
						data={[
							a.asset_id,
							a.asset_name,
							a.asset_name?.split('.').pop()?.toLowerCase() === 'pdf' ? 'PDF' : 'Imagen',
							moment.unix(a.date_created).format('DD/MM/YY HH:mm'),
							a.user_creator_name.toLocaleUpperCase(),
							<Dropdown icon={(
								<div className="fr label" style={{ padding: '3px 10px' }}>
									<Icon name='ellipsis' style={{ margin: 0 }} />
								</div>
								)} className='icon'>
									<Dropdown.Menu>
										<Dropdown.Item onClick={() => { setSelectedAsset(a); setShownModal(Modals.VIEW_ASSET); }}>
											<Icon name="eye" style={{ marginRight: 3 }} /> Ver 
										</Dropdown.Item>
										<Dropdown.Item onClick={() => { setSelectedAsset(a); setShownModal(Modals.DELETE_ASSET); }}>
											<Icon name="close" style={{ marginRight: 3 }} /> Eliminar 
										</Dropdown.Item>
									</Dropdown.Menu>
							</Dropdown>
						]}
					/>
				))
			) : (
				<Table.Cell row className='empty' colSpan={7}>
					No se encontraron registros
				</Table.Cell>
			)}
		</Table>
		<UserLog
			style={{ maxWidth: 500 }}
			user={expenseData}
		/>
		<Modal open={shownModal===Modals.DELETE} onClose={bindClose(setShownModal)} size='tiny'>
			<Modal.Header>Eliminar gasto</Modal.Header>
			{!!expenseData && <Modal.Content>
				<Header text='¿Eliminar gasto?' subheaderStyle={{ marginTop: 8, marginBottom: 15 }} />
				{expenseData &&<Table
					details
					titleSize='small'
					title='Detalles'
					data={[
						['ID', expenseData.expense_id],
						['Titulo', expenseData.expense_title],
						['Tipo', expenseData.type_name],
						['Método de pago', expenseData.method_name || <Icon name='minus'/>],
						['Departamento', expenseData.department_name ? <Label style={{ width: 110 }} color='orange'>{getDepartmentName(expenseData.department_id)}</Label> : <Icon name='minus'/>],
						['Estatus', (
							<div className={classNames("fr label", {
								green: expenseData.paid,
								red: expenseData.deleted,
								yellow: !expenseData.paid
							})}>
								{expenseData.deleted ? 'Eliminado' : expenseData.paid ? 'Pagado' : 'No Pagado'}
							</div>
						)],
						['Creador', expenseData.user_creator_name],
						['Monto', '$' + addCommas(expenseData.amount)]
					]}
				/>}
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic style={{ minWidth: 20 }} onClick={bindClose(setShownModal)} />
				<Button onClick={deleteExpense} text='Eliminar' color='red'/>
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.PAID} onClose={bindClose(setShownModal)} size='tiny'>
			<Modal.Header>Marcar como pagado</Modal.Header>
			{!!expenseData && <Modal.Content>
				<Header text='¿Marcar gasto como pagado?' subheaderStyle={{ marginTop: 8, marginBottom: 15 }} />
				{expenseData &&<Table
					details
					titleSize='small'
					title='Detalles'
					data={[
						['ID', expenseData.expense_id],
						['Titulo', expenseData.expense_title],
						['Tipo', expenseData.type_name],
						['Método de pago', expenseData.method_name || <Icon name='minus'/>],
						['Departamento', expenseData.department_name ? <Label style={{ width: 110 }} color='orange'>{getDepartmentName(expenseData.department_id)}</Label> : <Icon name='minus'/>],
						['Creador', expenseData.user_creator_name],
						['Monto', '$' + addCommas(expenseData.amount)]
					]}
				/>}
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic style={{ minWidth: 20 }} onClick={bindClose(setShownModal)} />
				<Button onClick={paid} text='Guardar' color='black'/>
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.UNPAID} onClose={bindClose(setShownModal)} size='tiny'>
			<Modal.Header>Desmarcar como pagado</Modal.Header>
			{!!expenseData && <Modal.Content>
				<Header text='¿Desmarcar gasto como pagado?' subheaderStyle={{ marginTop: 8, marginBottom: 15 }} />
				{expenseData &&<Table
					details
					titleSize='small'
					title='Detalles'
					data={[
						['ID', expenseData.expense_id],
						['Titulo', expenseData.expense_title],
						['Tipo', expenseData.type_name],
						['Método de pago', expenseData.method_name || <Icon name='minus'/>],
						['Departamento', expenseData.department_name ? <Label style={{ width: 110 }} color='orange'>{getDepartmentName(expenseData.department_id)}</Label> : <Icon name='minus'/>],
						['Creador', expenseData.user_creator_name],
						['Monto', '$' + addCommas(expenseData.amount)]
					]}
				/>}
				<Message type="error" list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cerrar' basic style={{ minWidth: 20 }} onClick={bindClose(setShownModal)} />
				<Button onClick={unpaid} text='Guardar' color='black'/>
			</Modal.Actions>
		</Modal>
		<ExpenseModal
			open={shownModal===Modals.EDIT}
			onClose={bindClose(setShownModal)}
			onUpdate={updateExpense}
			expenseToEdit={expenseData}
		/>
		<Modal open={shownModal===Modals.APPROVE} onClose={bindClick(setShownModal)} size='tiny'>
			<Modal.Header>¿Aprobar gasto?</Modal.Header>
			<Modal.Content>
				<Header text='¿Marcar gasto como aprobado?' subtext={'El gasto sera marcado como aprobado.'}/>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShownModal, Modals.APPROVE)} />
				<Button text='Guardar' color='black' onClick={approve}/>
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal===Modals.REJECT} onClose={bindClose(setShownModal)} size='tiny'>
			<Modal.Header>Rechazar gasto</Modal.Header>
			<Modal.Content>
				<Header text='¿Rechazar este gasto?' subtext='El gasto sera marcado como rechazado y se notificará al creador del gasto con el motivo de rechazo.' />
				<Dropdown
					selection
					fluid
					placeholder='Selecciona el motivo de rechazo'
					value={rejectReasonId}
					onChange={(e, { value }) => setRejectReasonId(value as number)}
					options={rejectReasons.map(r => ({
						key: r.reason_id,
						value: r.reason_id,
						text: r.reason,
					}))}
				/>
				<Message type="error" list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic style={{ minWidth: 20 }} onClick={bindClose(setShownModal)} />
				<Button onClick={rejectExpense} text='Rechazar' color='red'/>
			</Modal.Actions>
		</Modal>
		<AssetViewModal
			open={shownModal === Modals.VIEW_ASSET}
			onClose={bindClick(setShownModal, null)}
			asset={selectedAsset}
		/>
		<AssetViewModal
			open={shownModal === Modals.DELETE_ASSET}
			onClose={bindClick(setShownModal, null)}
			asset={selectedAsset}
			delete={true}
			onDeleted={onAssetDeleted}
		/>
		<AddAssetModal
			expenseId={expenseData.expense_id}
			open={shownModal===Modals.ADD_ASSET}
			onClose={bindClick(setShownModal, null)}
			onAdded={(a) => addAsset(a)}
		/>
	</div>
}

export default ExpenseView;